/**
 * Edit Ritual Page
 * Edit form with accordion sections for updating an existing ritual
 */

import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import { Loader2, MoreVertical, RefreshCw, Trash2, UserPlus } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomSelect } from "@/components/forms/customSelect"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { DateTimePicker } from "@/components/forms/dateTimePicker"
import { DurationPicker } from "@/components/forms/durationPicker"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { RadioSelect } from "@/components/forms/radioSelect"
import { TagsInput } from "@/components/forms/tagsInput"
import { InviteAcceptedIcon, InviteDeclinedIcon } from "@/components/icons"
import {
  type RitualOrganizer,
  SelectRitualOrganizersModal,
} from "@/components/modals/selectRitualOrganizersModal"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import { getAssetUrl } from "@/utils/assets"
import type { CourseModule } from "../components/CourseModulesSection"
import { CourseModulesSection } from "../components/CourseModulesSection"
import { CustomRepeatInput } from "../components/CustomRepeatInput"
import { RitualMediaSection } from "../components/ritualMediaSection"
import { useResendOrganizerInvite, useWithdrawOrganizerInvite } from "../hooks/useRitualInvites"
import { useUpdateRitual } from "../hooks/useRitualMutations"
import { useRitualOrganizers } from "../hooks/useRitualOrganizers"
import { type RitualFormData, ritualSchema } from "../schemas/ritual.schema"
import { getRitualById, type UpdateRitualData } from "../services/ritual.service"
import {
  RITUAL_TYPE_OPTIONS,
  RITUAL_VISIBILITY_OPTIONS,
  RitualTypeEnum,
  RitualVisibilityEnum,
} from "../types"
import type { Organizer, SectionValidationResult } from "../utils/accordionValidation"
import {
  validateDetailsSection,
  validateInfoSection,
  validateOrganizersSection,
  validatePrivacySection,
} from "../utils/accordionValidation"
import { getDeselectedValuesForVisibility, getPrivacyConfig } from "../utils/privacyConfig"

interface SelectOption {
  value: string
  label: string
}

// Helper to parse JSON arrays from backend (handles stringified arrays)
// Normalizes values to lowercase to match option values
const parseJsonArray = (value: string | string[] | undefined | null): string[] => {
  if (!value) return []
  if (Array.isArray(value)) return value.map(v => String(v).toLowerCase())
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map((v: string) => String(v).toLowerCase()) : []
  } catch {
    return []
  }
}

export function EditRitualScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Translations
  const { t } = useTranslation("rituals")

  // Get current user to filter out the creator from organizers
  const currentUser = useAuthStore(state => state.user)

  // Fetch ritual data
  const {
    data: ritual,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ritual", id],
    queryFn: () => getRitualById(id || ""),
    enabled: !!id,
  })

  // Fetch ritual organizers with pagination
  const {
    organizers: ritualOrganizers,
    isLoading: isLoadingOrganizers,
    hasNextPage,
    fetchNextPage,
  } = useRitualOrganizers({ ritualId: id || "" })

  // Resend and withdraw organizer invite hooks
  const { resendOrganizerInvite, isResending } = useResendOrganizerInvite(id || "")
  const { withdrawOrganizerInvite, isWithdrawing } = useWithdrawOrganizerInvite(id || "")

  // Initialize React Hook Form with Zod validation
  const form = useForm<RitualFormData>({
    resolver: zodResolver(ritualSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      title: "",
      type: RitualTypeEnum.IN_PERSON,
      visibility: RitualVisibilityEnum.PUBLIC,
      accessType: RitualVisibilityEnum.PUBLIC,
      dateTime: "",
      repeat: "none",
      repeatAt: "",
      repeatAtType: "DAYS",
      duration: "",
      address: "",
      meetingLink: "",
      steps: "",
      participationFee: "Free",
      price: "",
      feeDetails: "",
      minParticipantRequirement: false,
      minParticipants: "",
      registrationDeadline: "",
      description: "",
      whatToBring: "",
      whatYoullExperience: "",
      intention: "",
      websiteOrSocialMedia: "",
      tags: [],
      organizers: [],
      mediaFiles: {
        images: [],
        videos: [],
      },
      privacySettings: {
        visibility: ["everyone"],
        comment: ["owner"],
        edit: ["owner"],
        join: ["everyone"],
        invite: ["owner"],
        seeParticipantList: ["owner"],
      },
    },
  })

  const {
    register,
    control,
    formState: { errors },
    trigger,
    clearErrors,
    setValue,
    reset,
    getValues,
    watch,
  } = form

  // Ritual type options - imported from central types
  const ritualTypeOptions = RITUAL_TYPE_OPTIONS

  const repeatOptions: SelectOption[] = [
    { value: "none", label: t("repeat.options.none") },
    { value: "daily", label: t("repeat.options.daily") },
    { value: "weekly", label: t("repeat.options.weekly") },
    { value: "monthly", label: t("repeat.options.monthly") },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: t("repeat.options.custom") },
  ]

  const participationFeeOptions: SelectOption[] = [
    { value: "Free", label: t("createRitual.options.participationFee.free") },
    { value: "Paid", label: t("createRitual.options.participationFee.paid") },
    { value: "Donation-based", label: t("createRitual.options.participationFee.donation") },
  ]

  // Tag type with ID for API removal
  interface TagWithId {
    id: string
    name: string
  }

  // Tags state management
  const [tags, setTags] = useState<string[]>([])
  const [originalTagsWithIds, setOriginalTagsWithIds] = useState<TagWithId[]>([])

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    comment: ["owner"],
    edit: ["owner"],
    join: ["everyone"],
    invite: ["owner"],
    seeParticipantList: ["owner"],
  })

  // Privacy setting change handlers - use useCallback to prevent infinite loops
  const handleCommentChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, comment: options }))
      setValue("privacySettings.comment", options)
    },
    [setValue],
  )

  const handleEditChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, edit: options }))
      setValue("privacySettings.edit", options)
    },
    [setValue],
  )

  const handleJoinChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, join: options }))
      setValue("privacySettings.join", options)
    },
    [setValue],
  )

  const handleInviteChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, invite: options }))
      setValue("privacySettings.invite", options)
    },
    [setValue],
  )

  const handleSeeParticipantListChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, seeParticipantList: options }))
      setValue("privacySettings.seeParticipantList", options)
    },
    [setValue],
  )

  // Track previous visibility to detect changes
  const prevVisibilityRef = useRef<RitualVisibilityEnum | null>(null)

  // Update privacy settings when visibility changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: watch("visibility") is required for subscription
  useEffect(() => {
    const currentVisibility = watchedVisibility as RitualVisibilityEnum
    if (!currentVisibility) return

    if (prevVisibilityRef.current !== currentVisibility) {
      prevVisibilityRef.current = currentVisibility

      const currentPrivacySettings = {
        comment: getValues("privacySettings.comment"),
        edit: getValues("privacySettings.edit"),
        join: getValues("privacySettings.join"),
        invite: getValues("privacySettings.invite"),
        seeParticipantList: getValues("privacySettings.seeParticipantList"),
      }

      const deselected = getDeselectedValuesForVisibility(currentVisibility, currentPrivacySettings)

      setPrivacySettings({
        comment: deselected.comment,
        edit: deselected.edit,
        join: deselected.join,
        invite: deselected.invite,
        seeParticipantList: deselected.seeParticipantList,
      })

      setValue("privacySettings.comment", deselected.comment)
      setValue("privacySettings.edit", deselected.edit)
      setValue("privacySettings.join", deselected.join)
      setValue("privacySettings.invite", deselected.invite)
      setValue("privacySettings.seeParticipantList", deselected.seeParticipantList)
    }
  }, [watch("visibility"), setValue, getValues])

  // Selected organizers for ritual
  const [organizers, setOrganizers] = useState<Organizer[]>([])
  const [originalOrganizers, setOriginalOrganizers] = useState<Organizer[]>([])
  const [isOrganizerModalOpen, setIsOrganizerModalOpen] = useState(false)

  // Track API organizers marked for removal (will be removed when update is hit)
  const [organizersToRemove, setOrganizersToRemove] = useState<string[]>([])

  // Video URLs state (for YouTube/Vimeo links) - managed by RitualMediaSection
  const [videoUrls, setVideoUrls] = useState<
    Array<{
      id: string
      url: string
      thumbnail: string
      title?: string
      isEditing?: boolean
      isProcessed?: boolean
      isInvalid?: boolean
    }>
  >([])

  // Track original video URLs for change detection
  const [originalVideoUrls, setOriginalVideoUrls] = useState<
    Array<{
      id: string
      url: string
      thumbnail: string
      title?: string
    }>
  >([])

  const [mediaError, setMediaError] = useState<string | null>(null)

  // Course modules state (for SELF_GUIDED_COURSE)
  const [courseModules, setCourseModules] = useState<CourseModule[]>([])

  // Preserve conditional field values to prevent data loss when ritual type changes
  // When using register() with conditional rendering, unmounting resets values
  const [_preservedValues, setPreservedValues] = useState({
    meetingLink: "",
    steps: "",
  })

  // Module validation errors (for SELF_GUIDED_COURSE)
  const [moduleErrors, setModuleErrors] = useState<
    Record<string, { title?: string; description?: string; video?: string }>
  >({})

  // Course modules section error
  const [courseModulesError, setCourseModulesError] = useState<string | null>(null)

  // Organizers validation error
  const [organizersError, setOrganizersError] = useState<string | null>(null)

  // Section validation state
  const [sectionValidation, setSectionValidation] = useState<
    Record<string, SectionValidationResult>
  >({})

  // Track currently open accordion
  const [openAccordionId, setOpenAccordionId] = useState<string | null>("info")

  // Dynamic sidebar positioning based on actual accordion DOM position
  // Using refs and measurement to calculate real offset for each accordion
  const [sidebarOffset, setSidebarOffset] = useState(0)

  // Refs to track each accordion's DOM element
  const accordionRefs = useRef<Record<string, HTMLElement | null>>({})

  // Reference to the accordion container for offset calculations
  const accordionContainerRef = useRef<HTMLDivElement>(null)

  // Store ref for each accordion item
  const setAccordionRef = (id: string) => (element: HTMLDivElement | null) => {
    if (element) {
      accordionRefs.current[id] = element
    }
  }

  // Calculate the actual offset from container to the open accordion
  const updateSidebarOffset = useCallback(() => {
    if (!openAccordionId || !accordionContainerRef.current) {
      setSidebarOffset(0)
      return
    }

    const containerRect = accordionContainerRef.current.getBoundingClientRect()
    const accordionElement = accordionRefs.current[openAccordionId]

    if (accordionElement) {
      const accordionRect = accordionElement.getBoundingClientRect()
      // Calculate offset relative to the container's top edge
      const offset = accordionRect.top - containerRect.top
      setSidebarOffset(Math.max(0, offset))
    } else {
      setSidebarOffset(0)
    }
  }, [openAccordionId])

  // Update sidebar offset when open accordion changes
  useEffect(() => {
    // Small delay to ensure DOM has updated after accordion animation
    const timer = setTimeout(() => {
      updateSidebarOffset()
    }, 250) // Wait for accordion animation to complete

    return () => clearTimeout(timer)
  }, [updateSidebarOffset])

  // Recalculate on window resize to handle responsive changes
  useEffect(() => {
    const handleResize = () => {
      updateSidebarOffset()
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [updateSidebarOffset])

  // Initial calculation on mount when DOM is ready
  useEffect(() => {
    // Initial calculation after a brief delay to ensure DOM is ready
    const timer = setTimeout(() => {
      updateSidebarOffset()
    }, 100)

    return () => clearTimeout(timer)
  }, [updateSidebarOffset])

  // Handle accordion open change
  const handleAccordionOpenChange = (openItems: Set<string>) => {
    // Get the first (and only, since allowMultiple=false) open item
    const openItem = Array.from(openItems)[0]
    setOpenAccordionId(openItem || null)
  }

  // Ref for accordion
  const accordionRef = useRef<AccordionRef>(null)

  // Track blob URLs for cleanup on unmount only
  const blobUrlsRef = useRef<string[]>([])

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Mutation hook with automatic cache invalidation
  const updateRitualMutation = useUpdateRitual(id || "")

  // Initialize form with ritual data
  useEffect(() => {
    if (ritual) {
      // Format date for datetime-local input (convert UTC to local timezone)
      const dateTime = new Date(ritual.ritualTs)
      // Use local getter methods which automatically return local timezone values
      const year = dateTime.getFullYear()
      const month = String(dateTime.getMonth() + 1).padStart(2, "0")
      const day = String(dateTime.getDate()).padStart(2, "0")
      const hours = String(dateTime.getHours()).padStart(2, "0")
      const minutes = String(dateTime.getMinutes()).padStart(2, "0")
      const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`

      // Initialize course modules from ritual data (for SELF_GUIDED_COURSE)
      if (ritual.courseModules && ritual.courseModules.length > 0) {
        const modules: CourseModule[] = ritual.courseModules.map((cm, index) => ({
          id: cm.id || `new-${Date.now()}-${index}`, // Use "new" prefix for synthetic ids
          uploadId: cm.id && cm.upload ? "existing" : undefined, // Only mark as existing if it has both id and upload
          title: cm.title || "",
          description: cm.description || "",
          duration: cm.duration || "",
          order: cm.orderIndex !== undefined ? cm.orderIndex : index,
          isFree: cm.isFree ?? true,
          videoPreview: cm.upload?.filePath || undefined,
        }))
        setCourseModules(modules)
      }

      // Set media files BEFORE reset - compute mediaData first to include in reset
      const imageUploads = (ritual.uploads || []).filter(u => u.fileType?.startsWith("image/"))
      const videoUploads = (ritual.uploads || []).filter(u => u.fileType?.startsWith("video/"))

      const validImages = imageUploads.map(u => {
        const imageUrl = u.filePath?.startsWith("http") ? u.filePath : getAssetUrl(u.filePath)
        return {
          id: u.id,
          src: imageUrl,
          isCover: u.isCover || false,
          existingId: u.id,
        }
      })

      const validVideos = videoUploads.map(v => {
        const videoUrl = v.filePath?.startsWith("http") ? v.filePath : getAssetUrl(v.filePath)
        return {
          id: v.id,
          src: videoUrl,
          existingId: v.id,
        }
      })

      const mediaData = {
        images: validImages,
        videos: validVideos,
      }

      // Set tags - store full objects with IDs for removal
      const tagsWithIds = ritual.tags?.map(t => ({ id: t.id, name: t.tagText || t.name })) || []
      const tagNames = tagsWithIds.map(t => t.name).filter((n): n is string => n != null)

      // Set organizers - use data from API (with user/community details)
      const orgData = (ritual.organisers || []).map(org => {
        if (org.type === "user") {
          return {
            id: org.organiserId,
            type: org.type as "user" | "community",
            name: org.user?.name || t("createRitual.fallback.noName"),
            identifier: org.user?.username || null,
            avatar: org.user?.coverImage || org.user?.profilePictureUrl || undefined,
            roleId: "host",
            roleName: t("createRitual.roleHost"),
          }
        } else {
          return {
            id: org.organiserId,
            type: org.type as "user" | "community",
            name: org.community?.name || t("createRitual.fallback.community"),
            identifier: org.community?.handle || null,
            avatar: org.community?.coverImage || undefined,
            roleId: "host",
            roleName: t("createRitual.roleHost"),
          }
        }
      })

      // Reset form with ritual data - intentionally NOT including mediaFiles since MediaUploadSection manages its own state
      reset({
        title: ritual.title || "",
        type: ritual.ritualType || RitualTypeEnum.IN_PERSON,
        visibility: (ritual.accessType as RitualVisibilityEnum) || RitualVisibilityEnum.PUBLIC,
        accessType: ritual.accessType || RitualVisibilityEnum.PUBLIC,
        dateTime: dateTimeString,
        // Convert backend repeat value (uppercase) to lowercase for frontend
        repeat: ritual.repeat?.toLowerCase() || "none",
        duration: ritual.duration ? `${ritual.duration} min` : "",
        address: ritual.streetAddress || "",
        city: ritual.city || "",
        state: ritual.state || "",
        country: ritual.country || "",
        lat: ritual.lat != null ? Number(ritual.lat) : undefined,
        long: ritual.long != null ? Number(ritual.long) : undefined,
        meetingLink: ritual.meetingLink || "",
        steps: ritual.steps || "",
        participationFee: ritual.isDonationBased
          ? "Donation-based"
          : ritual.isPaid
            ? "Paid"
            : "Free",
        price: ritual.fee != null ? String(ritual.fee) : "",
        feeDetails: ritual.feeDetails || "",
        description: ritual.description || "",
        whatToBring: ritual.whatToBring || "",
        whatYoullExperience: ritual.experienceDetails || "",
        intention: ritual.intention || "",
        websiteOrSocialMedia: ritual.websiteLink || "",
        tags: tagNames,
        organizers: orgData,
        mediaFiles: mediaData, // Include mediaFiles in reset so form has initial values
        privacySettings: {
          visibility: parseJsonArray(ritual.whoCanSeeYourRitual) || ["everyone"],
          comment: parseJsonArray(ritual.whoCanComment) || ["owner"],
          edit: parseJsonArray(ritual.whoCanEditRitual) || ["owner"],
          join: parseJsonArray(ritual.whoCanJoin) || ["everyone"],
        },
        // Minimum participant requirement fields
        minParticipantRequirement: ritual.activateMinimumParticipantRequirement || false,
        minParticipants: ritual.minParticipants ? String(ritual.minParticipants) : "",
        registrationDeadline: ritual.registrationDeadline
          ? typeof ritual.registrationDeadline === "string"
            ? ritual.registrationDeadline.split("T")[0]
            : ritual.registrationDeadline.toISOString().split("T")[0]
          : "",
      })

      // Set tags state with full objects with IDs for removal
      setTags(tagNames)
      setOriginalTagsWithIds(tagsWithIds)

      // Set organizers state
      setOrganizers(orgData)
      setOriginalOrganizers(orgData)

      // Set media files in form for schema validation compliance
      setValue("mediaFiles", mediaData)

      // Initialize videoUrls from ritual videos if any
      // Note: This is for YouTube/Vimeo links, not uploaded video files
      const initialVideoUrls: Array<{
        id: string
        url: string
        thumbnail: string
        title?: string
        isEditing?: boolean
        isProcessed?: boolean
        isInvalid?: boolean
      }> = ritual.videos
        ? ritual.videos
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(video => ({
              id: video.id,
              url: video.videoUrl,
              thumbnail: video.thumbnailUrl || "",
              title: video.title || undefined,
              isEditing: false,
              isProcessed: true,
              isInvalid: false,
            }))
        : []

      setVideoUrls(initialVideoUrls)
      setOriginalVideoUrls(initialVideoUrls)

      // Set privacy settings - from ritual object (JSON strings that need parsing)
      const privacy = {
        visibility: parseJsonArray(ritual.whoCanSeeYourRitual) || ["everyone"],
        comment: parseJsonArray(ritual.whoCanComment) || ["owner"],
        edit: parseJsonArray(ritual.whoCanEditRitual) || ["owner"],
        join: parseJsonArray(ritual.whoCanJoin) || ["everyone"],
        invite: parseJsonArray(ritual.whoCanInvite) || ["owner"],
        seeParticipantList: parseJsonArray(ritual.whoCanSeeParticipantList) || ["owner"],
      }
      setPrivacySettings(privacy)
      setValue("privacySettings.join", privacy.join)
    }
  }, [ritual, reset, setValue, t])

  // This useEffect now only serves as a fallback/backup if onMediaChange's setValue doesn't work
  // The main sync happens in onMediaChange
  // Handlers
  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)
    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)
    trigger("tags")
  }

  // Handle adding organizers from modal (users and communities)
  const handleAddOrganizers = (newOrganizers: RitualOrganizer[]) => {
    const organizersWithType: Organizer[] = newOrganizers.map(org => ({
      id: org.id,
      type: org.type,
      name: org.name,
      identifier: org.identifier,
      avatar: org.profilePictureUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote: org.inviteNote,
    }))

    const updated = [...organizers, ...organizersWithType]
    setOrganizers(updated)
    setValue("organizers", updated)
    trigger("organizers")
    setOrganizersError(null)
    setIsOrganizerModalOpen(false)
  }

  // Handle marking an API organizer for removal (will be removed on update)
  const handleMarkOrganizerForRemoval = (organizerId: string) => {
    setOrganizersToRemove(prev => {
      if (prev.includes(organizerId)) {
        return prev
      }
      return [...prev, organizerId]
    })
  }

  // Course modules handlers (for SELF_GUIDED_COURSE)
  const handleAddModule = () => {
    const newModule: CourseModule = {
      id: `${Date.now()}-${Math.random()}`,
      title: "",
      description: "",
      duration: "",
      order: courseModules.length,
      isFree: true, // Default to true
    }
    setCourseModules([...courseModules, newModule])
    // Clear section error when a module is added
    setCourseModulesError(null)
  }

  const handleRemoveModule = (moduleId: string) => {
    const updated = courseModules.filter(m => m.id !== moduleId)
    // Reorder remaining modules
    const reordered = updated.map((module, index) => ({ ...module, order: index }))
    setCourseModules(reordered)
    // Clear errors for removed module
    setModuleErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[moduleId]
      return newErrors
    })
  }

  const handleReorderModules = (modules: CourseModule[]) => {
    setCourseModules(modules)
  }

  const handleUpdateModule = (
    moduleId: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => {
    const updated = courseModules.map(module => {
      if (module.id === moduleId) {
        // If fieldOrUpdates is a string, use old behavior (single field update)
        if (typeof fieldOrUpdates === "string") {
          return { ...module, [fieldOrUpdates]: value }
        }
        // Otherwise merge the partial updates
        return { ...module, ...fieldOrUpdates }
      }
      return module
    })
    setCourseModules(updated)

    // Clear errors for this module when it's updated
    setModuleErrors(prev => {
      const newErrors = { ...prev }
      if (newErrors[moduleId]) {
        // Clear specific field errors based on what was updated
        if (typeof fieldOrUpdates === "string") {
          delete newErrors[moduleId][fieldOrUpdates as keyof (typeof newErrors)[string]]
          // If no more errors for this module, remove it entirely
          if (Object.keys(newErrors[moduleId]).length === 0) {
            delete newErrors[moduleId]
          }
        }
      }
      return newErrors
    })
  }

  // Validate course modules - extracted as reusable function
  const validateCourseModules = useCallback(() => {
    // Only validate if ritual type is SELF_GUIDED_COURSE
    if (watch("type") !== RitualTypeEnum.SELF_GUIDED_COURSE) {
      return true
    }

    // Validate that at least one module exists
    if (courseModules.length === 0) {
      setCourseModulesError(t("createRitual.validation.atLeastOneModule"))
      setModuleErrors({})
      return false
    }

    // Clear section error if modules exist
    setCourseModulesError(null)

    // Validate each module and collect errors
    const errors: Record<string, { title?: string; description?: string; video?: string }> = {}
    let hasErrors = false

    courseModules.forEach(module => {
      const moduleErrors: { title?: string; description?: string; video?: string } = {}

      if (!module.title || module.title.trim() === "") {
        moduleErrors.title = t("createRitual.validation.moduleTitleRequired")
        hasErrors = true
      }

      if (!module.description || module.description.trim() === "") {
        moduleErrors.description = t("createRitual.validation.moduleDescriptionRequired")
        hasErrors = true
      }

      if (!module.videoFile && !module.uploadId) {
        moduleErrors.video = t("createRitual.validation.moduleVideoRequired")
        hasErrors = true
      }

      if (Object.keys(moduleErrors).length > 0) {
        errors[module.id] = moduleErrors
      }
    })

    setModuleErrors(errors)
    return !hasErrors
  }, [courseModules, watch, t])

  // Handle Next button - validate current section before allowing navigation
  const handleNext = async (currentIndex: number): Promise<boolean> => {
    const currentAccordionId = accordionItems[currentIndex]?.id
    if (!currentAccordionId) return true

    let fieldsToValidate: (keyof RitualFormData)[] = []

    switch (currentAccordionId) {
      case "info":
        fieldsToValidate = ["title", "type", "dateTime", "repeat", "duration", "participationFee"]
        if (watch("participationFee") === "Paid") {
          fieldsToValidate.push("price", "feeDetails")
        }
        if (watch("type") === RitualTypeEnum.IN_PERSON) {
          fieldsToValidate.push("address")
        }
        if (watch("type") === RitualTypeEnum.VIRTUAL_COURSE) {
          fieldsToValidate.push("meetingLink")
        }
        if (watch("minParticipantRequirement") === true) {
          fieldsToValidate.push("minParticipants", "registrationDeadline")
        }
        break
      case "details":
        fieldsToValidate = ["description", "tags"]
        if (watch("type") === RitualTypeEnum.SELF_GUIDED) {
          fieldsToValidate.push("steps")
        }
        break
      case "courseModules": {
        return validateCourseModules()
      }
      case "media": {
        // Media validation requires at least one image and a cover image
        const imageCount = (ritual?.uploads || []).filter(u =>
          u.fileType?.startsWith("image/"),
        ).length
        const hasCover = (ritual?.uploads || []).some(
          u => u.fileType?.startsWith("image/") && u.isCover,
        )
        if (imageCount === 0) {
          setMediaError(t("createRitual.validation.atLeastOneImage"))
          return false
        }
        if (!hasCover) {
          setMediaError(t("createRitual.validation.selectCoverImage"))
          return false
        }
        setMediaError(null)
        return true
      }
      case "organizers":
        // Organizers validation - at least one organizer required
        if (organizers.length === 0) {
          setOrganizersError(t("createRitual.validation.atLeastOneOrganizer"))
          return false
        }
        return true
      case "privacy":
        // Validate nested privacy settings - cast to string array for trigger
        return await trigger([
          "privacySettings.visibility",
          "privacySettings.comment",
          "privacySettings.edit",
          "privacySettings.join",
        ] as const)
    }

    const isValid = await trigger(fieldsToValidate)
    return isValid
  }

  // Watch specific form fields for validation (using individual watches to get fresh values)
  const watchedType = watch("type")
  const watchedTitle = watch("title")
  const watchedDateTime = watch("dateTime")
  const watchedRepeat = watch("repeat")
  const watchedDuration = watch("duration")
  const watchedAddress = watch("address")
  const watchedMeetingLink = watch("meetingLink")
  const watchedParticipationFee = watch("participationFee")
  const watchedPrice = watch("price")
  const watchedFeeDetails = watch("feeDetails")
  const watchedDescription = watch("description")
  const watchedSteps = watch("steps")
  const watchedTags = watch("tags")
  const watchedPrivacySettings = watch("privacySettings")
  const watchedVisibility = watch("visibility")
  const watchedPrivacySettingsStr = JSON.stringify(watchedPrivacySettings)

  // Validate all accordion sections on form change
  // biome-ignore lint/correctness/useExhaustiveDependencies: using JSON.stringify deps to prevent infinite re-renders from unstable object references while still reacting to actual data changes
  useEffect(() => {
    // Build form values from individual watches (ensures fresh values)
    const currentFormValues = {
      type: watchedType,
      title: watchedTitle,
      dateTime: watchedDateTime,
      repeat: watchedRepeat,
      duration: watchedDuration,
      address: watchedAddress,
      meetingLink: watchedMeetingLink,
      participationFee: watchedParticipationFee,
      price: watchedPrice,
      feeDetails: watchedFeeDetails,
      description: watchedDescription,
      steps: watchedSteps,
      tags: watchedTags,
      privacySettings: watchedPrivacySettings,
    }

    const validation: Record<string, SectionValidationResult> = {
      info: validateInfoSection(currentFormValues),
      details: validateDetailsSection(currentFormValues),
      media: (() => {
        const imageCount = (ritual?.uploads || []).filter(u =>
          u.fileType?.startsWith("image/"),
        ).length
        const hasCover = (ritual?.uploads || []).some(
          u => u.fileType?.startsWith("image/") && u.isCover,
        )
        const isComplete = imageCount > 0 && hasCover
        return {
          isComplete,
          summary: isComplete ? `${imageCount} photo${imageCount > 1 ? "s" : ""}` : undefined,
          error: !isComplete
            ? imageCount === 0
              ? "At least one image is required"
              : "Please select a cover image"
            : undefined,
        }
      })(),
      organizers: validateOrganizersSection(organizers, ritualOrganizers),
      privacy: validatePrivacySection(currentFormValues),
      // ADD: Course modules validation for SELF_GUIDED_COURSE
      ...(watchedType === RitualTypeEnum.SELF_GUIDED_COURSE
        ? {
            courseModules: {
              isComplete:
                courseModules.length > 0 &&
                courseModules.every(m => m.title && m.description && (m.videoFile || m.uploadId)),
              summary:
                courseModules.length > 0
                  ? `${courseModules.length} module${courseModules.length > 1 ? "s" : ""}`
                  : undefined,
            },
          }
        : {}),
    }

    setSectionValidation(validation)
  }, [
    watchedType,
    watchedTitle,
    watchedDateTime,
    watchedRepeat,
    watchedDuration,
    watchedAddress,
    watchedMeetingLink,
    watchedParticipationFee,
    watchedPrice,
    watchedFeeDetails,
    watchedDescription,
    watchedSteps,
    watchedTags,
    watchedPrivacySettingsStr,
    JSON.stringify(organizers),
    JSON.stringify(ritualOrganizers.map(o => o.id)),
    JSON.stringify(
      courseModules.map(m => ({
        title: m.title,
        description: m.description,
        videoFile: m.videoFile,
        uploadId: m.uploadId,
      })),
    ),
    ritual?.id,
  ])

  // Helper to convert privacy setting values to API format
  const toApiFormat = (values: string[]): string[] => {
    if (values.length === 0) return ["everyone"]
    return values.map(v => {
      const lower = v.toLowerCase()
      const mapping: Record<string, string> = {
        everyone: "everyone",
        owner: "owner",
        organisers: "organizers",
        organizers: "organizers",
        participants: "participants",
        anyone: "anyone",
        followers: "followers",
        community_members: "community_members",
        invite_req: "invite_req",
      }
      return mapping[lower] || lower
    })
  }

  // Handle update
  const handleUpdate = async (data: RitualFormData) => {
    if (!id) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Parse duration to get minutes (e.g., "30 min" -> 30)
      const durationMatch = data.duration.match(/(\d+)/)
      const durationMinutes = durationMatch ? parseInt(durationMatch[1], 10) : 30

      // Calculate tag changes
      const addedTags = tags.filter(t => !originalTagsWithIds.map(ot => ot.name).includes(t))
      const removedTagIds = originalTagsWithIds
        .filter(ot => !tags.includes(ot.name))
        .map(ot => ot.id)

      // Calculate organizer changes
      const addedOrganizers = organizers.filter(o => !originalOrganizers.find(oo => oo.id === o.id))
      const removedOrganizers = originalOrganizers
        .filter(o => !organizers.find(no => no.id === o.id))
        .map(o => ritual?.organisers?.find(ro => ro.organiserId === o.id)?.id)
        .filter(Boolean) as string[]

      // Calculate video URL changes
      const currentVideoUrls = videoUrls.filter(v => v.url && v.isProcessed && !v.isInvalid)
      const originalUrlsSet = new Set(originalVideoUrls.map(ov => ov.url))

      // New videos are those not in original set
      const addedVideoUrls = currentVideoUrls.filter(v => !originalUrlsSet.has(v.url))

      // Deleted videos are original URLs no longer in current
      const currentUrlsSet = new Set(currentVideoUrls.map(cv => cv.url))
      const deletedVideoUrls = originalVideoUrls
        .filter(ov => !currentUrlsSet.has(ov.url))
        .map(ov => ov.id)

      // Edited videos are those that exist in both but have changed (url, thumbnail, title, or order)
      const editedVideoUrls = currentVideoUrls.filter(cv => {
        const original = originalVideoUrls.find(ov => ov.url === cv.url)
        if (!original) return false
        // Check if anything changed
        return (
          cv.thumbnail !== original.thumbnail ||
          cv.title !== original.title ||
          videoUrls.indexOf(cv) !== originalVideoUrls.findIndex(ov => ov.url === cv.url)
        )
      })

      // Convert local datetime to proper ISO format (local timezone -> UTC)
      // Parse the datetime-local string manually - new Date("YYYY-MM-DDTHH:mm") treats as UTC!
      // We need to create Date from individual components to ensure local time interpretation
      const [datePart, timePart] = (data.dateTime || "").split("T")
      const [year, month, day] = datePart.split("-").map(Number)
      const [hours, minutes] = timePart.split(":").map(Number)
      // Create Date treating components as LOCAL time (month is 0-indexed, so month - 1)
      const localDate = new Date(year, month - 1, day, hours, minutes)
      // toISOString() correctly converts from local to UTC
      const localIsoTime = localDate.toISOString()

      // Check if this is a Personal ritual
      const isPersonal = data.visibility === RitualVisibilityEnum.PERSONAL

      // Prepare update data - only include fields that are visible for the visibility type
      const updateData: Partial<UpdateRitualData> = {
        title: data.title,
        type: data.type,
        accessType: data.accessType as RitualVisibilityEnum,
        date: localIsoTime,
        repeat: data.repeat,
        repeatAt: data.repeat === "custom" ? data.repeatAt : undefined,
        repeatAtType: data.repeat === "custom" ? data.repeatAtType : undefined,
        duration: durationMinutes,
        description: data.description || undefined,
        intention: data.intention || undefined,
        // Conditional fields - send explicit undefined to erase from DB when switching types
        // For VIRTUAL_COURSE, always send meetingLink (even if empty)
        // For other types, send undefined to erase it from DB
        meetingLink:
          data.type === RitualTypeEnum.VIRTUAL_COURSE ? data.meetingLink || undefined : undefined,
        // For SELF_GUIDED, always send steps (even if empty)
        // For other types, send undefined to erase it from DB
        steps: data.type === RitualTypeEnum.SELF_GUIDED ? data.steps || undefined : undefined,
      }

      // Only add public-facing fields for non-Personal rituals
      if (!isPersonal) {
        updateData.streetAddress = data.address || undefined
        updateData.city = data.city || undefined
        updateData.state = data.state || undefined
        updateData.country = data.country || undefined
        updateData.lat = data.lat
        updateData.long = data.long
        updateData.participationFee = data.participationFee
        updateData.price = data.price ? parseFloat(data.price) : undefined
        updateData.feeDetails = data.feeDetails || undefined
        updateData.whatToBring = data.whatToBring || undefined
        updateData.experienceDetails = data.whatYoullExperience || undefined
        updateData.webOrSocialMediaLink = data.websiteOrSocialMedia || undefined
      }

      // Add tags if changed
      if (addedTags.length > 0 || removedTagIds.length > 0) {
        updateData.tags = {
          add: addedTags.length > 0 ? addedTags : undefined,
          remove: removedTagIds.length > 0 ? removedTagIds : undefined,
        }
      }

      if (addedOrganizers.length > 0 || removedOrganizers.length > 0) {
        // For non-Personal rituals, handle organizer changes
        updateData.organizers = {
          add:
            addedOrganizers.length > 0
              ? addedOrganizers.map(o => ({
                  type: o.type as "user" | "community",
                  id: o.id,
                  inviteNote: o.inviteNote,
                }))
              : undefined,
          remove:
            removedOrganizers.length > 0 || organizersToRemove.length > 0
              ? [...removedOrganizers, ...organizersToRemove]
              : undefined,
        }
      }

      // Add privacy settings - use "only_me" for Personal rituals
      if (isPersonal) {
        updateData.whoCanSeeYourRitual = ["owner"]
        updateData.whoCanComment = ["owner"]
        updateData.whoCanEditRitual = ["owner"]
        updateData.whoCanJoin = ["invite_req"]
        updateData.whoCanInvite = ["owner"]
        updateData.whoCanSeeParticipantList = ["owner"]
      } else {
        updateData.whoCanSeeYourRitual = toApiFormat(data.privacySettings.visibility)
        updateData.whoCanComment = toApiFormat(data.privacySettings.comment)
        updateData.whoCanEditRitual = toApiFormat(data.privacySettings.edit)
        updateData.whoCanJoin = toApiFormat(data.privacySettings.join)
        updateData.whoCanInvite = toApiFormat(data.privacySettings.invite || ["organizers"])
        updateData.whoCanSeeParticipantList = toApiFormat(
          data.privacySettings.seeParticipantList || ["everyone"],
        )
      }

      // Add course modules for SELF_GUIDED_COURSE
      if (data.type === RitualTypeEnum.SELF_GUIDED_COURSE) {
        updateData.courseModules = courseModules.map(m => ({
          id: m.uploadId ? m.id : undefined, // Only include id for existing modules
          title: m.title,
          description: m.description,
          duration: m.duration,
          order: m.order,
          isFree: m.isFree,
          videoFile: m.videoFile,
        }))
      } else {
        // Empty array signals deletion of all course modules when switching away
        updateData.courseModules = []
      }

      // Add video URL changes
      if (addedVideoUrls.length > 0 || deletedVideoUrls.length > 0 || editedVideoUrls.length > 0) {
        updateData.videos = {
          add:
            addedVideoUrls.length > 0
              ? addedVideoUrls.map(v => ({
                  video_url: v.url,
                  thumbnail_url: v.thumbnail || undefined,
                  title: v.title || undefined,
                }))
              : undefined,
          edit:
            editedVideoUrls.length > 0
              ? editedVideoUrls.map((v, index) => ({
                  id: v.id,
                  video_url: v.url,
                  thumbnail_url: v.thumbnail || undefined,
                  title: v.title || undefined,
                  sort_order: index,
                }))
              : undefined,
          delete: deletedVideoUrls.length > 0 ? deletedVideoUrls : undefined,
        }
      }

      // Minimum participant requirement fields
      updateData.activateMinimumParticipantRequirement = data.minParticipantRequirement
      updateData.minParticipants = data.minParticipantRequirement
        ? data.minParticipants
          ? parseInt(data.minParticipants, 10)
          : undefined
        : undefined
      updateData.registrationDeadline = data.minParticipantRequirement
        ? data.registrationDeadline || undefined
        : undefined

      // Call mutation with automatic cache invalidation
      await updateRitualMutation.mutateAsync(updateData)

      // Clear the organizers to remove list
      setOrganizersToRemove([])

      // Navigate back to detail page
      navigate(`/rituals/${id}`)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("createRitual.errors.failedToUpdate"))
    } finally {
      setIsSubmitting(false)
    }
  }

  // Contextual tips for each section
  const getSectionTips = useCallback((sectionId: string | null) => {
    const tipsMap: Record<string, { title: string; tips: string[]; showPreview?: boolean }> = {
      info: {
        title: "Info",
        tips: [
          "Use a clear, descriptive title",
          "Avoid special symbols or emojis in the title",
          "Choose the correct ritual type",
          "Set an accurate date and duration",
          "Add a full address for in-person rituals",
          "Use repeat if the ritual happens regularly",
        ],
      },
      details: {
        title: "Details",
        tips: [
          "Describe the ritual clearly and step by step",
          "Mention any items participants should bring",
          "Share what participants will experience",
          "Explain the intention or purpose of the ritual",
          "Avoid links or promotion in the description",
          "Use relevant tags to help others discover it",
        ],
      },
      organizers: {
        title: "Organizer",
        tips: [
          "Add co-organizers if the ritual is led by multiple people or communities",
          "Invite ritualists who actively guide the experience",
          "Ensure each organizer has a complete profile",
          "Only add organizers who are involved",
          "You can manage or remove organizers later",
        ],
      },
      media: {
        title: "Upload Media",
        tips: [
          "Upload a clear cover image that reflects the ritual atmosphere",
          "Use natural lighting and calm, authentic visuals",
          "Avoid text or logos on images",
          "Keep images high quality and relevant to the ritual",
          "Add a video if it helps explain the experience",
          "Keep media high quality and relevant to the ritual",
        ],
        showPreview: true,
      },
      privacy: {
        title: "Privacy",
        tips: [
          "Choose who can see your ritual before publishing",
          "Limit comments if you want a quieter space",
          "Allow edits only for trusted organizers",
          "Use request to join for smaller or private groups",
          "Review privacy settings before sharing the ritual",
        ],
      },
      courseModules: {
        title: "Course Modules",
        tips: [
          "Add at least one module with title, description, and video",
          "Organize modules in a logical sequence",
          "Keep video content focused and relevant",
          "Set appropriate duration for each module",
          "Preview module content before publishing",
        ],
      },
    }

    return tipsMap[sectionId || "info"] || tipsMap.info
  }, [])

  // Cleanup blob URLs ONLY on component unmount (empty deps array)
  useEffect(() => {
    return () => {
      // Revoke all blob URLs created during the component's lifetime
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url)
      }
    }
  }, [])

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">Failed to load ritual: {(error as Error).message}</p>
      </div>
    )
  }

  // Accordion items data - defined as function for dynamic filtering based on visibility
  const getAccordionItems = (): AccordionItem[] => {
    const baseAccordionItems: AccordionItem[] = [
      // INFO ACCORDION
      {
        id: "info",
        title: t("createRitual.accordion.info"),
        userTitle: watch("title"), // Display user's title in collapsed view
        showNextButton: true,
        isComplete: sectionValidation.info?.isComplete,
        hasError: !sectionValidation.info?.isComplete,
        error: sectionValidation.info?.error,
        summary: sectionValidation.info?.summary,
        price: sectionValidation.info?.price,
        content: (
          <div className="space-y-6">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.basicInfoDescription")}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6.25">
              {/* TITLE - Full width */}
              <div className="md:col-span-2">
                <CustomInput
                  {...register("title")}
                  id="title"
                  label={t("createRitual.labels.title")}
                  required
                  placeholder={t("createRitual.placeholders.title")}
                  error={errors.title?.message}
                  onClearError={() => clearErrors("title")}
                />
              </div>

              {/* VISIBILITY */}
              <div className="md:col-span-2 flex flex-col gap-2.5">
                <label className="text-xs leading-5 text-body uppercase">
                  {t("createRitual.labels.type")}
                  <span className="ms-1">*</span>
                </label>
                <div className="flex flex-wrap items-center gap-7.5">
                  {RITUAL_VISIBILITY_OPTIONS.map(option => (
                    <label key={option.value} className="flex items-center gap-2.5 cursor-pointer">
                      <div className="relative flex size-6 items-center justify-center">
                        <input
                          type="radio"
                          name="visibility"
                          value={option.value}
                          disabled
                          checked={watch("visibility") === option.value}
                          onChange={e => {
                            const newVisibility = e.target.value as RitualVisibilityEnum
                            setValue("visibility", newVisibility)
                            clearErrors("visibility")
                            // Update accessType to match visibility (uppercase format for backend)
                            setValue(
                              "accessType",
                              newVisibility.toUpperCase() as "PUBLIC" | "PRIVATE" | "PERSONAL",
                            )
                          }}
                          className="peer sr-only"
                        />
                        <span className="absolute inset-0 rounded-full border-2 border-brand-olive" />
                        <span className="size-3 rounded-full fill-brand-olive bg-brand-olive text-brand-olive opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold leading-6.25 text-color-brand-offwhite">
                          {option.label}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.visibility && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.visibility.message}
                  </p>
                )}
              </div>

              {/* FORMAT - Hide for PERSONAL visibility */}
              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <RadioSelect
                  value={watch("type")}
                  onChange={value => {
                    // Preserve current conditional field values before type change
                    const currentMeetingLink = watch("meetingLink") || ""
                    const currentSteps = watch("steps") || ""

                    setValue("type", value as RitualTypeEnum)
                    clearErrors("type")

                    // Store values in state to persist across type changes
                    setPreservedValues({
                      meetingLink: currentMeetingLink,
                      steps: currentSteps,
                    })
                  }}
                  options={ritualTypeOptions}
                  label={t("createRitual.labels.format")}
                  required
                  error={errors.type?.message}
                  onClearError={() => clearErrors("type")}
                  disabled
                />
              )}

              {/* DATE & TIME - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {watch("type") !== "SELF_GUIDED" && watch("type") !== "SELF_GUIDED_COURSE" && (
                <DateTimePicker
                  value={watch("dateTime")}
                  onChange={value => {
                    setValue("dateTime", value)
                    clearErrors("dateTime")
                  }}
                  label={t("createRitual.labels.dateTime")}
                  required
                  showTime
                  error={errors.dateTime?.message}
                  onClearError={() => clearErrors("dateTime")}
                  disabled
                />
              )}

              {/* REPEAT - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {watch("type") !== "SELF_GUIDED" && watch("type") !== "SELF_GUIDED_COURSE" && (
                <RadioSelect
                  value={watch("repeat")}
                  onChange={value => {
                    setValue("repeat", value)
                    clearErrors("repeat")
                  }}
                  options={repeatOptions}
                  label={t("createRitual.labels.repeat")}
                  required
                  error={errors.repeat?.message}
                  onClearError={() => clearErrors("repeat")}
                  disabled
                />
              )}

              {/* CUSTOM REPEAT - Hidden for SELF_GUIDED and SELF_GUIDED_COURSE */}
              {watch("type") !== "SELF_GUIDED" &&
                watch("type") !== "SELF_GUIDED_COURSE" &&
                watch("repeat") === "custom" && (
                  <div className="md:col-span-2 space-y-2">
                    <CustomRepeatInput
                      control={control}
                      error={errors.repeatAt?.message || errors.repeatAtType?.message}
                      onClearError={() => {
                        clearErrors("repeatAt")
                        clearErrors("repeatAtType")
                      }}
                      disabled
                    />
                    <p className="text-sm text-muted-foreground">{t("repeat.specifyRepeat")}</p>
                  </div>
                )}

              {/* DURATION */}
              <DurationPicker
                value={watch("duration")?.replace(/\s*min\s*/i, "") || ""}
                onChange={value => {
                  setValue("duration", value)
                  clearErrors("duration")
                }}
                label={t("createRitual.labels.duration")}
                required
                error={errors.duration?.message}
                disabled
                onClearError={() => clearErrors("duration")}
              />

              {/* ADDRESS - Only for IN_PERSON and not PERSONAL visibility */}
              {watch("type") === RitualTypeEnum.IN_PERSON &&
                watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                  <div className="md:col-span-2">
                    <AutocompleteInput
                      value={watch("address")}
                      onLocationSelect={locationData => {
                        setValue("address", locationData.address)
                        setValue("city", locationData.city || "")
                        setValue("state", locationData.state || "")
                        setValue("country", locationData.country || "")
                        setValue("lat", locationData.lat)
                        setValue("long", locationData.lng)
                        clearErrors("address")
                      }}
                      onInputChange={value => {
                        setValue("address", value)
                        clearErrors("address")
                      }}
                      label={t("createRitual.labels.address")}
                      placeholder={t("createRitual.placeholders.enterLocation")}
                      error={errors.address?.message}
                      onClearError={() => clearErrors("address")}
                    />
                  </div>
                )}

              {/* MEETING LINK - Only for VIRTUAL_COURSE */}
              {watch("type") === RitualTypeEnum.VIRTUAL_COURSE && (
                <div className="md:col-span-2">
                  <CustomInput
                    {...register("meetingLink", {
                      onChange: e => {
                        // Update preserved value when input changes
                        setPreservedValues(prev => ({ ...prev, meetingLink: e.target.value }))
                      },
                    })}
                    id="meetingLink"
                    label={t("createRitual.labels.meetingLink")}
                    required
                    placeholder="https://meet.google.com/xxx or https://zoom.us/j/xxx"
                    error={errors.meetingLink?.message}
                    onClearError={() => clearErrors("meetingLink")}
                  />
                </div>
              )}

              {/* PARTICIPATION FEE - Hide for PERSONAL visibility */}
              {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
                <>
                  <CustomSelect
                    value={watch("participationFee")}
                    onChange={e => {
                      setValue(
                        "participationFee",
                        e.target.value as "Free" | "Paid" | "Donation-based",
                      )
                      clearErrors("participationFee")
                    }}
                    options={participationFeeOptions}
                    label={t("createRitual.labels.participationFee")}
                    required
                    error={errors.participationFee?.message}
                    onClearError={() => clearErrors("participationFee")}
                  />

                  {/* PRICE - Only shown when Paid is selected */}
                  {watch("participationFee") === "Paid" && (
                    <div className="">
                      <CustomInput
                        {...register("price", {
                          onChange: e => {
                            const value = e.target.value
                            // Prevent negative values
                            if (value && parseFloat(value) < 0) {
                              setValue("price", "0")
                            }
                          },
                        })}
                        id="price"
                        label={t("createRitual.labels.price")}
                        type="number"
                        min="0"
                        step="1"
                        prefix="$"
                        hideSpinButtons
                        required
                        placeholder="10"
                        error={errors.price?.message}
                        onClearError={() => clearErrors("price")}
                        disabled
                      />
                    </div>
                  )}

                  {/* FEE DETAILS - Only shown when Paid */}
                  {["Paid"].includes(watch("participationFee")) && (
                    <CustomTextarea
                      {...register("feeDetails")}
                      label={t("createRitual.labels.feeDetails")}
                      placeholder={t("createRitual.placeholders.feeDetailsAlt")}
                      error={errors.feeDetails?.message}
                      onClearError={() => clearErrors("feeDetails")}
                      disabled
                    />
                  )}
                </>
              )}

              {/* MINIMUM PARTICIPANT REQUIREMENT - Always shown */}
              <div className="flex items-center gap-2.5 md:col-span-2">
                <Checkbox
                  id="minParticipantRequirement"
                  checked={watch("minParticipantRequirement")}
                  onCheckedChange={checked =>
                    setValue("minParticipantRequirement", checked === true)
                  }
                  className="bg-transparent w-6 h-6"
                />
                <label
                  htmlFor="minParticipantRequirement"
                  className="text-sm font-semibold leading-6.25 cursor-pointer select-none"
                >
                  Activate minimum participant requirement
                </label>
              </div>

              {/* MINIMUM NUMBER OF PARTICIPANTS REQUIRED - Conditional */}
              {watch("minParticipantRequirement") && (
                <CustomInput
                  {...register("minParticipants")}
                  id="minParticipants"
                  label={t("createRitual.labels.minParticipants")}
                  required
                  type="number"
                  min="1"
                  step="1"
                  placeholder={t("createRitual.placeholders.minParticipants")}
                  error={errors.minParticipants?.message}
                  onClearError={() => clearErrors("minParticipants")}
                />
              )}

              {/* REGISTRATION DEADLINE - Conditional */}
              {watch("minParticipantRequirement") && (
                <DateTimePicker
                  value={watch("registrationDeadline")}
                  onChange={value => {
                    setValue("registrationDeadline", value)
                    clearErrors("registrationDeadline")
                  }}
                  label={t("createRitual.labels.registrationDeadline")}
                  required
                  showTime={false}
                  error={errors.registrationDeadline?.message}
                  onClearError={() => clearErrors("registrationDeadline")}
                />
              )}
            </div>
          </div>
        ),
      },

      // DETAILS ACCORDION
      {
        id: "details",
        title: t("createRitual.accordion.details"),
        showNextButton: true,
        isComplete: sectionValidation.details?.isComplete,
        hasError: !sectionValidation.details?.isComplete,
        error: sectionValidation.details?.error,
        summary: sectionValidation.details?.summary,
        content: (
          <div className="space-y-6.25">
            <p className="text-sm mb-7.5 text-body">{t("createRitual.detailsDescription")}</p>

            {/* DESCRIPTION */}
            <CustomTextarea
              {...register("description")}
              id="description"
              label={t("createRitual.labels.description")}
              required
              placeholder={t("createRitual.placeholders.describeWhatHappens")}
              error={errors.description?.message}
              onClearError={() => clearErrors("description")}
            />

            {/* WHAT TO BRING, WHAT YOU'LL EXPERIENCE, WEBSITE - Hide for PERSONAL visibility */}
            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <>
                <CustomTextarea
                  {...register("whatToBring")}
                  id="whatToBring"
                  label={t("createRitual.labels.whatToBring")}
                  placeholder={t("createRitual.placeholders.whatToBringAlt")}
                  error={errors.whatToBring?.message}
                  onClearError={() => clearErrors("whatToBring")}
                />

                <CustomTextarea
                  {...register("whatYoullExperience")}
                  id="whatYoullExperience"
                  label={t("createRitual.labels.whatYoullExperience")}
                  placeholder={t("createRitual.placeholders.whatYouExperienceAlt")}
                  error={errors.whatYoullExperience?.message}
                  onClearError={() => clearErrors("whatYoullExperience")}
                />
              </>
            )}

            {/* INTENTION */}
            <CustomTextarea
              {...register("intention")}
              id="intention"
              label={t("createRitual.labels.intention")}
              placeholder={t("createRitual.placeholders.intentionAlt")}
              error={errors.intention?.message}
              onClearError={() => clearErrors("intention")}
            />

            {/* STEPS TO PERFORM - Only for SELF_GUIDED */}
            {watch("type") === RitualTypeEnum.SELF_GUIDED && (
              <CustomTextarea
                {...register("steps", {
                  onChange: e => {
                    // Update preserved value when input changes
                    setPreservedValues(prev => ({ ...prev, steps: e.target.value }))
                  },
                })}
                id="steps"
                label={t("createRitual.labels.stepsToPerform")}
                required
                rows={4}
                placeholder={t("createRitual.placeholders.steps")}
                error={errors.steps?.message}
                onClearError={() => clearErrors("steps")}
              />
            )}

            {/* WEBSITE OR SOCIAL MEDIA - Hide for PERSONAL visibility */}
            {watch("visibility") !== RitualVisibilityEnum.PERSONAL && (
              <CustomInput
                {...register("websiteOrSocialMedia")}
                id="websiteOrSocialMedia"
                label={t("createRitual.labels.websiteSocial")}
                placeholder="https://your-website.com"
                error={errors.websiteOrSocialMedia?.message}
                onClearError={() => clearErrors("websiteOrSocialMedia")}
              />
            )}

            {/* TAGS */}
            <TagsInput
              id="ritual-tags"
              label={t("createRitual.labels.tags")}
              tags={tags}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              placeholder={t("createRitual.placeholders.tagsAlt")}
              error={errors.tags?.message}
              onClearError={() => clearErrors("tags")}
              enableSearch={true}
            />
          </div>
        ),
      },

      // ORGANIZERS ACCORDION
      {
        id: "organizers",
        title: t("createRitual.accordion.organizers"),
        showNextButton: true,
        isComplete: sectionValidation.organizers?.isComplete,
        hasError: !sectionValidation.organizers?.isComplete,
        error: organizersError ?? undefined,
        summary: sectionValidation.organizers?.summary,
        displayName: sectionValidation.organizers?.displayName,
        userPreviews: sectionValidation.organizers?.userPreviews,
        showViewMore: ritualOrganizers.length > 3,
        content: (
          <div className="space-y-6">
            <p className="text-body">{t("createRitual.organizersDescription")}</p>
            {/* Loading State */}
            {isLoadingOrganizers && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
            {/* Organizers List */}
            {!isLoadingOrganizers && (ritualOrganizers.length > 0 || organizers.length > 0) && (
              <div className="space-y-4">
                {/* API Organizers - sort by owner first, then filter out removed */}
                {ritualOrganizers
                  .filter(org => !organizersToRemove.includes(org.id))
                  .sort((a, b) => {
                    const aIsOwner = ritual?.createdBy ? a.organiserId === ritual.createdBy : false
                    const bIsOwner = ritual?.createdBy ? b.organiserId === ritual.createdBy : false
                    if (aIsOwner && !bIsOwner) return -1
                    if (!aIsOwner && bIsOwner) return 1
                    return 0
                  })
                  .map(org => {
                    const isUser = org.organiserType === "user"
                    const avatar = isUser
                      ? org.user?.coverImage || org.user?.profilePictureUrl
                      : org.community?.coverImage
                    const name = isUser
                      ? org.user?.name || t("createRitual.fallback.noName")
                      : org.community?.name || t("createRitual.fallback.community")
                    const identifier = isUser ? org.user?.username : org.community?.handle

                    // Check if this organizer is the creator/owner
                    const isOwner = ritual?.createdBy ? org.organiserId === ritual.createdBy : false

                    // Determine status label
                    const getStatusLabel = () => {
                      if (isOwner) return "Owner"
                      switch (org.status) {
                        case "PENDING":
                          return "Invited"
                        case "JOINED":
                          return "Joined"
                        case "DECLINED":
                          return "Declined"
                        default:
                          return org.status
                      }
                    }

                    const statusLabel = getStatusLabel()

                    return (
                      <div key={org.id} className="flex items-center gap-4">
                        {/* Avatar - 48px with themed background */}
                        <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {avatar ? (
                            <img src={avatar} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-semibold">{name?.[0]?.toUpperCase() || "?"}</span>
                          )}
                        </div>

                        {/* Organizer Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold truncate">
                              {name || t("createRitual.fallback.noName")}
                            </p>
                          </div>
                          <p className="text-sm truncate">
                            {identifier ? `@${identifier}` : identifier}
                          </p>
                        </div>

                        {/* Status Label and Actions */}
                        <div className="flex items-center gap-2">
                          {/* Status Tag - same styling as modal */}
                          <span className="flex items-center gap-1.5 text-xs text-white">
                            {isOwner || org.status === "PENDING" ? (
                              <img
                                src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
                                alt=""
                                className="w-3.5 h-3.5"
                              />
                            ) : org.status === "JOINED" ? (
                              <InviteAcceptedIcon size={14} />
                            ) : org.status === "DECLINED" ? (
                              <InviteDeclinedIcon size={14} />
                            ) : null}
                            {statusLabel}
                          </span>
                          {/* Dropdown Menu - only for non-owners */}
                          {!isOwner && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 flex-shrink-0"
                                >
                                  <MoreVertical size={18} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {/* Remove - for DECLINED and JOINED only (NOT for PENDING/INVITED) */}
                                {org.status !== "PENDING" && (
                                  <DropdownMenuItem
                                    onClick={() => handleMarkOrganizerForRemoval(org.id)}
                                    className="text-red focus:text-red cursor-pointer"
                                  >
                                    <Trash2 size={16} className="mr-2" />
                                    Remove
                                  </DropdownMenuItem>
                                )}
                                {/* Resend - for Declined only */}
                                {org.status === "DECLINED" && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      resendOrganizerInvite({ organizerId: org.id })
                                    }}
                                    disabled={isResending}
                                    className="cursor-pointer"
                                  >
                                    <RefreshCw size={16} className="mr-2" />
                                    Resend
                                  </DropdownMenuItem>
                                )}
                                {/* Resend + Withdraw - for PENDING only */}
                                {org.status === "PENDING" && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        resendOrganizerInvite({ organizerId: org.id })
                                      }}
                                      disabled={isResending}
                                      className="cursor-pointer"
                                    >
                                      <RefreshCw size={16} className="mr-2" />
                                      Resend
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        withdrawOrganizerInvite(org.id)
                                      }}
                                      disabled={isWithdrawing}
                                      className="text-red focus:text-red cursor-pointer"
                                    >
                                      <Trash2 size={16} className="mr-2" />
                                      Withdraw
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    )
                  })}
                {/* Locally Added Organizers (not yet saved) */}
                {organizers.map(org => {
                  // Skip if this organizer is already in ritualOrganizers (by id)
                  const alreadyExists = ritualOrganizers.some(o => o.id === org.id)
                  if (alreadyExists) return null

                  return (
                    <div key={org.id} className="flex items-center gap-4">
                      {/* Avatar - 48px with themed background */}
                      <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {org.avatar ? (
                          <img
                            src={org.avatar}
                            alt={org.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-semibold">
                            {org.name?.[0]?.toUpperCase() || "?"}
                          </span>
                        )}
                      </div>

                      {/* Organizer Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold truncate">
                            {org.name || t("createRitual.fallback.noName")}
                          </p>
                        </div>
                        <p className="text-sm truncate">
                          {org.identifier ? `@${org.identifier}` : org.identifier}
                        </p>
                      </div>

                      {/* Status Label and Actions */}
                      <div className="flex items-center gap-2">
                        {/* Status Tag - "Invited" with person icon, same styling as modal */}
                        <span className="flex items-center gap-1.5 text-xs text-white">
                          <img
                            src={`${import.meta.env.VITE_ASSET_BASE_URL}add_person.svg`}
                            alt=""
                            className="w-3.5 h-3.5"
                          />
                          Invited
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                // Remove locally added organizer
                                const updated = organizers.filter(o => o.id !== org.id)
                                setOrganizers(updated)
                                setValue("organizers", updated)
                                trigger("organizers")
                              }}
                              className="text-red focus:text-red cursor-pointer"
                            >
                              <Trash2 size={16} className="mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  )
                })}
                {/* Load More Button */}
                {hasNextPage && (
                  <Button
                    variant="ghost"
                    onClick={() => fetchNextPage()}
                    className="w-full text-sm text-stone"
                  >
                    Load more organizers
                  </Button>
                )}
              </div>
            )}
            {/* Empty State */}
            {!isLoadingOrganizers && ritualOrganizers.length === 0 && organizers.length === 0 && (
              <p className="text-sm text-stone">No organizers yet</p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4">
              {/* Invite Organizer Button */}
              <Button
                variant="ghost"
                onClick={() => setIsOrganizerModalOpen(true)}
                className="flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
              >
                <UserPlus size={18} />
                {t("createRitual.buttons.inviteOrganizer")}
              </Button>
            </div>
          </div>
        ),
      },

      // MEDIA ACCORDION
      {
        id: "media",
        title: t("createRitual.accordion.uploadMedia"),
        showNextButton: true,
        isComplete: sectionValidation.media?.isComplete,
        hasError: !sectionValidation.media?.isComplete || mediaError !== null,
        error: sectionValidation.media?.error ?? mediaError ?? undefined,
        summary: sectionValidation.media?.summary,
        content: (
          <div className="space-y-4">
            <p className="text-sm text-body mb-5">{t("createRitual.mediaDescription")}</p>
            <RitualMediaSection
              ritualId={ritual?.id || ""}
              onVideoUrlsChange={videos => setVideoUrls(videos)}
              initialVideoUrls={videoUrls}
              showVideoUrlInput={true}
              showAudioSection={true}
              maxFiles={4}
              maxFileSize={5 * 1024 * 1024}
              maxAudioFileSize={100 * 1024 * 1024}
              enableAiGeneration={true}
              aiEntityType="ritual"
              aiEntityContext={{
                name: watchedTitle,
                description: watchedDescription,
                type: watchedType,
              }}
            />
          </div>
        ),
      },

      // COURSE MODULES ACCORDION - Only for SELF_GUIDED_COURSE
      ...(watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE
        ? [
            {
              id: "courseModules",
              title: t("createRitual.accordion.courseModules"),
              showNextButton: true,
              isComplete:
                courseModules.length > 0 &&
                courseModules.every(m => m.title && m.description && (m.videoFile || m.uploadId)),
              hasError: courseModulesError !== null || Object.keys(moduleErrors).length > 0,
              error: courseModulesError ?? undefined,
              summary:
                courseModules.length > 0
                  ? `${courseModules.length} module${courseModules.length > 1 ? "s" : ""}`
                  : undefined,
              content: (
                <div className="space-y-4">
                  <p className="text-body">{t("createRitual.courseModulesDescription")}</p>
                  <CourseModulesSection
                    modules={courseModules}
                    onAddModule={handleAddModule}
                    onRemoveModule={handleRemoveModule}
                    onReorderModules={handleReorderModules}
                    onUpdateModule={handleUpdateModule}
                    moduleErrors={moduleErrors}
                    sectionError={courseModulesError}
                  />
                </div>
              ),
            },
          ]
        : []),

      // PRIVACY ACCORDION
      {
        id: "privacy",
        title: t("createRitual.accordion.privacy"),
        isComplete: sectionValidation.privacy?.isComplete,
        hasError: !sectionValidation.privacy?.isComplete,
        error: sectionValidation.privacy?.error,
        summary: sectionValidation.privacy?.summary,
        content: (() => {
          const visibility = watch("visibility") as RitualVisibilityEnum
          const privacyConfig = getPrivacyConfig(visibility)
          return (
            <div className="space-y-6">
              <p className="text-sm mb-7 text-body">{t("createRitual.privacyDescription")}</p>

              <div className="space-y-6">
                {/* WHO CAN JOIN */}
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanJoin")}
                  options={privacyConfig.join.options}
                  selectedOptions={privacySettings.join}
                  onChange={handleJoinChange}
                  disabledOptions={privacyConfig.join.disabledOptions}
                  autoSelectedOptions={privacyConfig.join.autoSelected}
                  groupExclusion={privacyConfig.join.groupExclusion}
                />
                {errors.privacySettings?.join && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.join.message}
                  </p>
                )}

                {/* WHO CAN INVITE */}
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanInvite")}
                  options={privacyConfig.invite.options}
                  selectedOptions={privacySettings.invite}
                  onChange={handleInviteChange}
                  disabledOptions={privacyConfig.invite.disabledOptions}
                  autoSelectedOptions={privacyConfig.invite.autoSelected}
                />
                {errors.privacySettings?.invite && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.invite.message}
                  </p>
                )}

                {/* WHO CAN COMMENT */}
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanComment")}
                  options={privacyConfig.comment.options}
                  selectedOptions={privacySettings.comment}
                  onChange={handleCommentChange}
                  disabledOptions={privacyConfig.comment.disabledOptions}
                  autoSelectedOptions={privacyConfig.comment.autoSelected}
                />
                {errors.privacySettings?.comment && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.comment.message}
                  </p>
                )}

                {/* WHO CAN EDIT */}
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanEdit")}
                  options={privacyConfig.edit.options}
                  selectedOptions={privacySettings.edit}
                  onChange={handleEditChange}
                  disabledOptions={privacyConfig.edit.disabledOptions}
                  autoSelectedOptions={privacyConfig.edit.autoSelected}
                />
                {errors.privacySettings?.edit && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.edit.message}
                  </p>
                )}

                {/* WHO CAN SEE PARTICIPANT LIST */}
                <MultiSelectDropdown
                  label={t("createRitual.privacySettings.whoCanSeeParticipantList")}
                  options={privacyConfig.seeParticipantList.options}
                  selectedOptions={privacySettings.seeParticipantList}
                  onChange={handleSeeParticipantListChange}
                  disabledOptions={privacyConfig.seeParticipantList.disabledOptions}
                  autoSelectedOptions={privacyConfig.seeParticipantList.autoSelected}
                  selectAllWith={privacyConfig.seeParticipantList.selectAllWith}
                />
                {errors.privacySettings?.seeParticipantList && (
                  <p className="text-xs text-brand-terracotta-bright">
                    {errors.privacySettings.seeParticipantList.message}
                  </p>
                )}
              </div>
            </div>
          )
        })(),
      },
    ]

    // Filter out Organizers and Privacy accordions for PERSONAL rituals
    const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL
    const items = baseAccordionItems.filter(item => {
      if (isPersonal) {
        return item.id !== "organizers" && item.id !== "privacy"
      }
      return true
    })

    return items
  }

  // Get accordion items - recomputed on each render to handle conditional accordions
  const accordionItems = getAccordionItems()

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-360 mx-auto px-5 md:px-17.5 py-10">
        {/* Main Content - 2 Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Accordions (70%) */}
          <div className="lg:col-span-2 max-w-[850px]" ref={accordionContainerRef}>
            <Accordion
              ref={accordionRef}
              items={accordionItems}
              defaultOpenIndex={0}
              allowMultiple={false}
              onNextClick={handleNext}
              onItemClose={closedItemId => {
                if (closedItemId === "courseModules") {
                  // Defer validation to avoid updating state during render
                  setTimeout(() => {
                    validateCourseModules()
                  }, 0)
                }
              }}
              onOpenChange={handleAccordionOpenChange}
              accordionItemRef={setAccordionRef}
            />
          </div>

          {/* Right Column - Sidebar (30%) */}
          <div className="sticky top-4">
            {/* Spacer div to align tips with active accordion - Uses dynamic offset */}
            <div
              style={{
                height: `${sidebarOffset}px`,
                transition: "height 250ms ease-in-out",
              }}
            />

            {/* Contextual Tips Card */}
            <div
              className={cn(
                "rounded-lg border-1 px-6 pb-6 pt-3 transition-colors bg-card border-card",
              )}
              style={{
                transition: "background-color 250ms ease-in-out, border-color 250ms ease-in-out",
              }}
            >
              <div>
                <h3 className="text-xl font-semibold text-primary">
                  {getSectionTips(openAccordionId).title} Tips
                </h3>
              </div>
              <ul className="space-y-2 text-sm">
                {getSectionTips(openAccordionId).tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-[14px] md:text-[15px] text-primary">{tip}</span>
                  </li>
                ))}
              </ul>

              {/* Media Preview Card - shown only for Upload Media section */}
              {openAccordionId === "media" &&
                getSectionTips("media").showPreview &&
                (() => {
                  const images = (ritual?.uploads || []).filter(u =>
                    u.fileType?.startsWith("image/"),
                  )
                  if (images.length === 0) return null
                  const coverImage = images.find(img => img.isCover) || images[0]
                  const previewUrl = coverImage.filePath?.startsWith("http")
                    ? coverImage.filePath
                    : getAssetUrl(coverImage.filePath)
                  return (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-body mb-2">Cover Preview</p>
                      <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                        <img
                          src={previewUrl}
                          alt="Cover preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )
                })()}
            </div>

            {/* Action Card */}
            <div className="border bg-card border-card rounded-lg p-5 mt-4">
              {/* Error Message */}
              {submitError && (
                <div className="p-3 rounded-md">
                  <p className="text-sm text-brand-terracotta-bright">{submitError}</p>
                </div>
              )}

              {/* Update Button */}
              <Button
                onClick={async () => {
                  const isDisabled = isSubmitting || accordionItems.some(item => item.error)

                  if (isDisabled) {
                    return
                  }

                  const isValid = await form.trigger()

                  if (isValid) {
                    await handleUpdate(form.getValues())
                  }
                }}
                disabled={isSubmitting || accordionItems.some(item => item.error)}
                className="w-full uppercase text-sm"
                variant="secondary"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin mr-2" />
                    {t("editRitual.updating")}
                  </>
                ) : (
                  t("editRitual.updateRitual")
                )}
              </Button>

              {/* Cancel Button */}
              <Button
                onClick={() => navigate(`/rituals/${id}`)}
                disabled={isSubmitting}
                className="w-full mt-4 uppercase text-sm"
                variant="outline"
              >
                {t("buttons.cancel")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Organizer Selection Modal */}
      {isOrganizerModalOpen && (
        <SelectRitualOrganizersModal
          isOpen={isOrganizerModalOpen}
          onClose={() => setIsOrganizerModalOpen(false)}
          onAddOrganizers={handleAddOrganizers}
          existingOrganizerIds={organizers.map(org => org.id)}
          currentUserId={currentUser?.id}
          ritualId={id}
        />
      )}
    </div>
  )
}
